// What the models made of a recording: its summary, then every event in time order — a behaviour
// read off the tracks (somebody staying, a vehicle stopped, a crowd, people after hours, a vehicle
// seen before) or a threat the vision model saw (a weapon, fire or smoke, a fight, a person down),
// each with the frame it was checked on, the model's verdict and its reason. "Play" runs the
// event's stretch in the player; a click on the row opens its frame large with every fact.
// Pure: the events and the play action arrive as props.
import type { TFunction } from 'i18next'
import { Play, ShieldAlert } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Thumbnail } from '@/components/humint/Thumbnail'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { wallSpan } from '@/features/document-overlay/recording-time'
import type { VideoEvent, VideoEventVerdict } from '@/services/api/video'
import { cn } from '@/utils/cn'
import { formatVideoTime } from '@/utils/video-file'
import { CropDetail, type CropFact } from './CropDetail'

const COPY = 'documents.detail.videoEvents'
const CROP = 'documents.detail.crop'

/** What a recording event is: a behaviour read off the tracks or a threat the vision model saw. */
export const VIDEO_EVENT_KINDS = [
  'loitering',
  'vehicle_stopped',
  'crowd',
  'after_hours',
  'repeat_vehicle',
  'weapon',
  'fire_smoke',
  'fight',
  'person_down',
] as const

const VERDICT_BADGE = {
  confirmed: 'warning',
  rejected: 'muted',
  unverified: 'outline',
} as const satisfies Record<VideoEventVerdict, 'warning' | 'muted' | 'outline'>

/** An event's kind in the reader's language; a kind this build does not know reads as "Event". */
export function useEventKindLabel() {
  const { t } = useTranslation()
  return (kind: string) => t(`${COPY}.kinds.${kind}`, { defaultValue: t(`${COPY}.kinds.other`) })
}

export interface VideoEventsInRecordingProps {
  summary: string | null
  events: readonly VideoEvent[]
  /** "Play" on an event: the player runs its stretch. Given only when a player is on the page. */
  onPlaySpan?: (from: number, to: number) => void
  /** When the recording starts (ISO 8601): each event then shows its time on the camera's clock too. */
  recordedAt?: string | null
}

/** The stretch on the camera's clock beside its place in the recording; nothing without a clock. */
export function WallSpan({ span }: { span: string | null }) {
  const { t } = useTranslation()
  if (!span) return null
  return (
    <span className="text-foreground/80" title={t('documents.detail.video.recording.wallClock')}>
      {` · ${span}`}
    </span>
  )
}

/** Who or what an event is about: a person, the people of a whole scene, or a vehicle's class. */
function subjectOf(label: string, t: TFunction): string {
  if (label === 'person') return t(`${CROP}.subjectPerson`)
  if (label === 'scene') return t(`${CROP}.subjectScene`)
  return t(`documents.detail.video.objectClass.${label}`, { defaultValue: label })
}

/** Everything known of an event, for its large view. */
function useEventFacts(event: VideoEvent, recordedAt: string | null): CropFact[] {
  const { t, i18n } = useTranslation()
  const wall = wallSpan(recordedAt, event.t_start, event.t_end, i18n.language)
  const facts: CropFact[] = [
    {
      label: t(`${CROP}.recording`),
      value: t(`${COPY}.span`, {
        from: formatVideoTime(event.t_start),
        to: formatVideoTime(event.t_end),
      }),
    },
  ]
  if (wall) facts.push({ label: t(`${CROP}.cameraClock`), value: wall })
  facts.push(
    { label: t(`${CROP}.duration`), value: formatVideoTime(event.t_end - event.t_start) },
    {
      label: t(`${CROP}.verdict`),
      value: t(`${COPY}.verdict.${event.verdict}`, { defaultValue: event.verdict }),
    },
    { label: t(`${CROP}.subject`), value: subjectOf(event.label, t) }
  )
  if (event.count > 1) facts.push({ label: t(`${CROP}.people`), value: event.count })
  if (event.detail) facts.push({ label: t(`${CROP}.detail`), value: event.detail })
  if (event.track_ids.length > 0)
    facts.push({
      label: t(`${CROP}.tracks`),
      value: event.track_ids.map(track => `#${track}`).join(', '),
    })
  return facts
}

function EventRow({
  event,
  expanded,
  onToggle,
  onPlaySpan,
  recordedAt,
}: {
  event: VideoEvent
  expanded: boolean
  onToggle: () => void
  onPlaySpan?: (from: number, to: number) => void
  recordedAt: string | null
}) {
  const { t, i18n } = useTranslation()
  const kindLabel = useEventKindLabel()
  const facts = useEventFacts(event, recordedAt)
  return (
    <li
      className={cn(
        'flex flex-col gap-3 rounded-md border bg-card px-2 py-2',
        expanded ? 'border-primary' : 'border-border'
      )}
      data-event-kind={event.kind}
      data-event-verdict={event.verdict}
      data-expanded={expanded ? 'true' : 'false'}
    >
      <div className="flex gap-3">
        <button
          type="button"
          aria-expanded={expanded}
          title={t(expanded ? `${CROP}.close` : `${CROP}.open`)}
          onClick={onToggle}
          className="flex min-w-0 flex-1 gap-3 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {expanded ? null : (
            <Thumbnail
              url={event.keyframe_path}
              alt={kindLabel(event.kind)}
              Icon={ShieldAlert}
              className="size-14"
            />
          )}
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="flex flex-wrap items-center gap-1.5">
              <span className="text-sm font-medium text-foreground">{kindLabel(event.kind)}</span>
              <Badge variant={VERDICT_BADGE[event.verdict] ?? 'outline'}>
                {t(`${COPY}.verdict.${event.verdict}`, { defaultValue: event.verdict })}
              </Badge>
            </span>
            <span className="text-xs tabular-nums text-muted-foreground">
              {t(`${COPY}.span`, {
                from: formatVideoTime(event.t_start),
                to: formatVideoTime(event.t_end),
              })}
              <WallSpan span={wallSpan(recordedAt, event.t_start, event.t_end, i18n.language)} />
              {event.count > 1 ? ` · ${t(`${COPY}.people`, { count: event.count })}` : ''}
            </span>
            {event.detail && !expanded ? (
              <span className="text-xs text-foreground">{event.detail}</span>
            ) : null}
            {event.reason && !expanded ? (
              <span className="line-clamp-3 text-xs text-muted-foreground">{event.reason}</span>
            ) : null}
          </span>
        </button>
        {onPlaySpan ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="shrink-0 self-start"
            aria-label={t(`${COPY}.play`)}
            onClick={() => onPlaySpan(Math.max(0, event.t_start - 1), event.t_end + 1)}
          >
            <Play className="size-4" aria-hidden />
            <span className="max-md:sr-only">{t(`${COPY}.play`)}</span>
          </Button>
        ) : null}
      </div>
      {expanded ? (
        <CropDetail
          shape="frame"
          image={{ url: event.keyframe_path, alt: kindLabel(event.kind), Icon: ShieldAlert }}
          facts={facts}
        >
          {event.reason ? <p className="text-sm text-foreground">{event.reason}</p> : null}
        </CropDetail>
      ) : null}
    </li>
  )
}

export function VideoEventsInRecording({
  summary,
  events,
  onPlaySpan,
  recordedAt = null,
}: VideoEventsInRecordingProps) {
  const { t } = useTranslation()
  // One event open at a time: opening another closes the first.
  const [openId, setOpenId] = useState<string | null>(null)
  return (
    <div className="flex flex-col gap-3" data-testid="video-events">
      {summary ? (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">{t(`${COPY}.summary`)}</span>
          <p className="whitespace-pre-line text-sm text-foreground">{summary}</p>
        </div>
      ) : null}
      {events.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t(`${COPY}.none`)}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {events.map(event => (
            <EventRow
              key={event.event_id}
              event={event}
              expanded={event.event_id === openId}
              onToggle={() =>
                setOpenId(current => (current === event.event_id ? null : event.event_id))
              }
              onPlaySpan={onPlaySpan}
              recordedAt={recordedAt}
            />
          ))}
        </ul>
      )}
    </div>
  )
}
